import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import { getCreatureOffsets } from '../gameData';
import AnimatedFish from './AnimatedFish';

export default class Critter extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene,startPt,critterType, critterID) {
    super(scene, startPt?.x, startPt?.y);

    this.critterType = critterType;
    this.typeID = critterID;

    this.homePos = null;
    this.startPos = null;
    this.curPos = startPt;
    this.destPos = null;
    this.vX = null;
    this.vY = null;
    this.curDirection = 1;
    this.speed = 42;
    this.ease = 0.14;

    // Straight moves
    this.distanceToTarget = null;
    this.distanceTraveled = null;

    this.hoverPoint = null;
    this.hoverRange = null;
    this.borders = null;
    this.maxTop = null;
    this.maxLeft = null;
    this.maxBottom = null;
    this.maxRight = null;

    this.swimState = null;

    this.isHighlighted = false;
    this.isScared = false;

    if (!this.critterType) this.critterType = 'Fish';
    if (!this.typeID) this.typeID = 1;
    const offsets = getCreatureOffsets(this.typeID);
    this.animatedSprite = new AnimatedFish(this.scene, this.critterType, this.typeID, offsets);
    this.add(this.animatedSprite);
    this.swimState = null;
    const bounds = this.getBounds();
    this.list.forEach((g) => {
      const child = g;
      child.x -= bounds.width / 2;
      child.y -= bounds.height / 2;
    });
    this.setBorders();

    ABCyaPhaser.Container.setContainerInteractive(this);
    this.scene.input.setDraggable(this, true);
  }

  setBorders () {
    this.borders = [0,0,this.scene.scale.width,this.scene.scale.height];
  }

  reset (startPt,critterType, critterID) {
    this.swimState = null;
    this.isHighlighted = false;
    this.curPos = startPt;
    this.x = this.curPos.x;
    this.y = this.curPos.y;
    this.critterType = critterType;
    this.typeID = critterID;
    const offsets = getCreatureOffsets(this.typeID);
    this.animatedSprite.switchFish(this.typeID, offsets);
    this.animatedSprite.setPosition(0,0);
    const bounds = this.getBounds();
    this.list.forEach((g) => {
      const child = g;
      child.x -= bounds.width / 2;
      child.y -= bounds.height / 2;
    });

    ABCyaPhaser.Container.setContainerInteractive(this);
  }

  initializeHover () {
    this.swimState = SWIM_HOVER;
    this.speed = 2;
    this.ease = 0.02;
    this.hoverRange = 13;
    this.borders = [0,0,this.scene.scale.width,this.scene.scale.height];
    this.maxLeft = this.x - this.hoverRange;
    this.maxTop = this.y - this.hoverRange * 2;
    this.maxRight = this.x + this.hoverRange;
    this.maxBottom = this.y + this.hoverRange * 2;

    this.hoverPoint = { x: this.x, y: this.y };
    this.startPos = { x: this.x, y: this.y };
    this.curPos = { x: this.x, y: this.y };
    const destX = Phaser.Math.Between(this.maxLeft, this.maxRight);
    const destY = Phaser.Math.Between(this.maxTop, this.maxBottom);
    this.destPos = { x: destX, y: destY };
  }

  doAnimatedSwim () {
    this.animatedSprite.startSwimming();
  }

  highlight () {
    this.isHighlighted = true;
    this.animatedSprite.highlight();
  }

  unhighlight () {
    this.isHighlighted = false;
    this.animatedSprite.unhighlight();
  }

  roamTank (delta) {
    if (this.vX >= 0) { // Moving right
      if (this.curPos.x > this.borders[2]) {
        this.vX *= -1; // turn left
        this.speed = Phaser.Math.Between(20,80);
        this.borders[0] = Phaser.Math.Between(0,this.scene.scale.width / 6);
        this.scene.tweens.killTweensOf(this);
        ABCyaPhaser.Tweens.addTween(this, 200, { scaleX: -1 }, Phaser.Math.Easing.Linear);
      }
    }
    else { // Moving left
      if (this.curPos.x < this.borders[0]) {
        this.vX *= -1;// turning right
        this.speed = Phaser.Math.Between(20,80);
        this.borders[2] = Phaser.Math.Between(this.scene.scale.width / 6, this.scene.scale.width);
        this.scene.tweens.killTweensOf(this);
        ABCyaPhaser.Tweens.addTween(this, 200, { scaleX: 1 }, Phaser.Math.Easing.Linear);
      }
    }

    if (this.vY <= 0) { // Moving up
      if (this.curPos.y < this.borders[1]) {
        this.vY *= -1; // turning down
        this.borders[3] = Phaser.Math.Between(this.scene.scale.height / 6, this.scene.scale.height);
      }
    }
    else { // Moving down
      if (this.curPos.y > this.borders[3]) {
        this.vY *= -1; // turning up
        this.borders[1] = Phaser.Math.Between(0, this.scene.scale.height / 6);
      }
    }
    this.curPos.x += this.vX * delta;
    this.curPos.y += this.vY * delta;
  }

  swimStraight () {
    const workingX = this.curPos.x + Math.cos(this.directionAngle) * this.speed;
    const workingY = this.curPos.y + Math.sin(this.directionAngle) * this.speed;
    this.distanceTraveled = Phaser.Math.Distance.BetweenPoints(this.startPos, { x: workingX,y: workingY });
    if (this.distanceTraveled >= this.distanceToTarget) {
      if (this.swimState === SWIM_HOVER) {
        this.pickNewHoverSpot();
      }
      else {
        this.swimState = null;
        this.emit('FISH_ARRIVED_TO_DEST');
      }
    }
    else {
      this.curPos.x = workingX;
      this.curPos.y = workingY;
    }
  }

  pickNewHoverSpot () {
    this.startPos = { x: this.x, y: this.y };
    this.curPos = { x: this.x, y: this.y };
    const destX = Phaser.Math.Between(this.maxLeft, this.maxRight);
    const destY = Phaser.Math.Between(this.maxTop, this.maxBottom);
    this.destPos = { x: destX, y: destY };

    this.ease = 0.006;
  }

  easeSwim () {
    const dX = this.destPos.x - this.curPos.x;
    const dY = this.destPos.y - this.curPos.y;
    const distance = Phaser.Math.Distance.BetweenPoints(this.curPos, this.destPos);
    if (distance > 1) {
      this.curPos.x += dX * this.ease;
      this.curPos.y += dY * this.ease;
    }
    else {
      this.curPos.x = this.destPos.x;
      this.curPos.y = this.destPos.y;
      if (this.swimState === SWIM_HOVER) {
        this.pickNewHoverSpot();
      }
      else if (this.swimState === SWIM_TO_POINT) {
        this.swimState = null;
        this.emit('FISH_ARRIVED_TO_DEST');
      }
    }
  }

  initializeSwimTo (destX, destY) {
    this.swimState = SWIM_TO_POINT;
    this.startPos = { x: this.x, y: this.y };
    this.curPos = { x: this.x, y: this.y };
    this.destPos = { x: destX, y: destY };
    this.speed = 13;
    let direction = Phaser.Math.Angle.BetweenPointsY(this.curPos, this.destPos);
    if (direction === 0) {
      direction = this.direction;
    }
    direction = direction / Math.abs(direction);
    if (direction !== this.direction) {
      this.direction = direction;
      this.scene.tweens.killTweensOf(this);
      ABCyaPhaser.Tweens.addTween(this, 200, { scaleX: this.direction }, Phaser.Math.Easing.Linear);
    }
    this.ease = 0.2;
    this.directionAngle = Math.atan2(destY - this.curPos.y, destX - this.curPos.x);
    this.distanceToTarget = Phaser.Math.Distance.BetweenPoints(this.curPos, this.destPos);
    this.distanceTraveled = 0;
  }

  stopSwimming () {
    this.swimState = null;
  }

  turnDir (direction) {
    if (direction !== this.direction) {
      this.direction = direction;
      this.scene.tweens.killTweensOf(this);
      ABCyaPhaser.Tweens.addTween(this, 200, { scaleX: this.direction }, Phaser.Math.Easing.Linear);
    }
  }

  tick (delta) {
    // Swimming
    if (this.swimState) {
      switch (this.swimState) {
        case SWIM_ROAM:
          this.roamTank(delta);
          break;
        case SWIM_TO_POINT:
          this.swimStraight();
          break;
        case SWIM_HOVER:
          this.easeSwim();
          break;
      }
    }
    this.x = this.curPos.x;
    this.y = this.curPos.y;
    this.animatedSprite.tick(delta);
  }
}

const SWIM_TO_POINT = 1;
const SWIM_HOVER = 2;
const SWIM_STOP = 3;
const SWIM_ROAM = 4;